import Preference from './Preference';

/** MySQL JSON columns are sometimes returned as a JSON string instead of a parsed array. */
export function normalizeTeacherTagsFromApi(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.filter((t): t is string => typeof t === 'string').map((t) => t.trim()).filter(Boolean);
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.filter((t): t is string => typeof t === 'string').map((t) => t.trim()).filter(Boolean);
      }
    } catch {
      return [trimmed];
    }
  }
  return [];
}

// Interface for raw teacher data from API
export interface RawTeacherData {
  id: number;
  institution_id: number;
  teacher_code: string;
  name: string;
  email: string;
  tags?: string[] | string;
  preferences?: any[]; // Will be converted to Preference[]
  qualifiedSubjects?: Array<{
    id: number;
    subject_code: string;
    name: string;
    color?: string;
  }>;
  createdAt?: string;
  updatedAt?: string;
}

class Teacher {
  id: number;
  institutionId: number;
  teacherCode: string;
  private _name: string;
  email: string;
  tags: string[];
  preferences: Preference[];
  qualifiedSubjects: Array<{
    id: number;
    subjectCode: string;
    name: string;
    color?: string;
  }>;
  createdAt?: string;
  updatedAt?: string;

  constructor(data: RawTeacherData) {
    this.id = data.id;
    this.institutionId = data.institution_id;
    this.teacherCode = data.teacher_code;
    this._name = data.name;
    this.email = data.email;
    this.tags = normalizeTeacherTagsFromApi(data.tags);
    this.preferences = data.preferences ? Preference.fromRawDataArray(data.preferences) : [];
    this.qualifiedSubjects = data.qualifiedSubjects ? data.qualifiedSubjects.map(subject => ({
      id: subject.id,
      subjectCode: subject.subject_code,
      name: subject.name,
      color: subject.color
    })) : [];
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
  }

  get name(): string {
    return this._name;
  }

  toRawData(): RawTeacherData {
    return {
      id: this.id,
      institution_id: this.institutionId,
      teacher_code: this.teacherCode,
      name: this.name,
      email: this.email,
      tags: this.tags,
      preferences: this.preferences.map(pref => pref.toRawData()),
      qualifiedSubjects: this.qualifiedSubjects.map(subject => ({
        id: subject.id,
        subject_code: subject.subjectCode,
        name: subject.name,
        color: subject.color
      })),
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }

  static fromRawDataArray(rawData: RawTeacherData[]): Teacher[] {
    return rawData.map(data => new Teacher(data));
  }
}

export default Teacher; 